
import { unref } from "vue";
import { createWebHistory, createRouter } from "vue-router";

import useAuthStore from "../stores/AuthStore";
import useActiveProviderStore from "../stores/ActiveProviderStore";


// Default pages
import DashboardPage from "./views/DashboardPage.vue";
import LoginPage from "./views/LoginPage.vue";

const routes = [
    {
        path: "/login",
        name: "Login",
        component: LoginPage,
        meta:{
            title : 'Login to your account',
            layout : 'MINIMAL',
            public : true,
        },
    },

    {
        path: "/password",
        name: "Password",
        component: ()=>import('./views/PasswordPage.vue'),
        meta:{
            title : 'Reset your password',
            layout : 'MINIMAL',
            public : true,
        },
    },
    {
        path: "/referrer/register/:type(engineer|building-manager|agent|referrer|demand-generator)",
        name: "ReferrerRegister",
        component: ()=>import('../global-components/ReferrerRegister.vue'),
        meta:{
            title : 'Register as a Champion',
            layout : 'MINIMAL',
            public : true,
        },
    },
    {
        path: "/recover",
        name: "Recover",
        component: ()=>import('./views/RecoverPage.vue'),
        meta:{
            title : 'Set a new password',
            layout : 'MINIMAL',
            public : true,
        },
    },

    {
        path: "/authenticate-payment/:payment_id",
        name: "authenticatepayment",
        component: ()=>import('./views/AuthenticatePaymentPage.vue'),
        meta:{
            title : 'Authenticate Payment',
            layout : 'MINIMAL',
            public : true,
        },
    },

    //todo - this functionality is for spotio checkout referral
    {
        path: "/checkout/:subscription_intent_id",
        name: "checkout",
        component: ()=>import('./views/CheckoutPage.vue'),
        meta:{
            title : 'Complete order',
            layout : 'MINIMAL',
            public : true,
        },
    },


    {
        path: "/apply",
        component: ()=>import('./views/ApplyPage.vue'),
        children:[
        {
            path: '',
            name: "apply",
            component: ()=>import('./views/Apply/ApplyPostcodePage.vue'),
            meta:{
                title : 'Enter your postcode',
                layout : 'MINIMAL',
                public : true,
            },
        },
        {
            path: "packages",
            name: "apply.plan",
            component: ()=>import('./views/Apply/ApplyPlanPage.vue'),
            meta:{
                title : 'Select a package',
                layout : 'MINIMAL',
                public : true,
            },
        },
        {
            path: "packages/addons",
            name: "apply.addons",
            component: ()=>import('./views/Apply/ApplyAddOnsPage.vue'),
            props: true,
            meta:{
                title : 'Addons',
                layout : 'MINIMAL',
                public : true,
            }
        },
        {
            path: "addons",
            name: "apply.existing-addons",
            component: ()=>import('./views/Apply/ApplyAddOnsExistingPage.vue'),
            props: true,
            meta:{
                title : 'Your account',
                layout : 'MINIMAL',
                public : true,
            },
        },
        {
            path: "account",
            name: "apply.account",
            component: ()=>import('./views/Apply/ApplyAccountPage.vue'),
            meta:{
                title : 'Your account',
                layout : 'MINIMAL',
                public : true,
            },
        },
        {
            path: "checkout",
            name: "apply.checkout",
            component: ()=>import('./views/Apply/ApplyCheckoutPage.vue'),
            meta:{
                title : 'Complete your order',
                layout : 'MINIMAL',
                public : true,
            },
        },
        ]
    },

    // HOME
    {
        path: "/",
        name: "home",
        component: DashboardPage,
        meta:{
            title : 'Portal',
        },
    },
    {
        path: "/order/:checkoutId",
        name: "order",
        component: ()=>import('./views/OrderPage.vue'),
        meta:{
            title : 'Your order',
        },
    },
    {
        path: "/billing",
        name: "billing",
        component: ()=>import('./views/BillingPage.vue'),
        meta:{
            title : 'Billing History',
        },
    },
    {
        path: "/billing/invoices/:invoice_id",
        name: "invoice",
        component: ()=>import('./views/InvoicePage.vue'),
        meta:{
            title : 'View invoice',
        },
    },
    {
        path: "/billing/invoices/:invoice_id/print",
        name: "print_invoice",
        component: ()=>import('./views/PrintInvoicePage.vue'),
        meta:{
            title : 'Print invoice',
            layout : 'MINIMAL',
        },
    },
    {
        path: "/billing/payments/:payment_id",
        name: "payment",
        component: ()=>import('./views/PaymentPage.vue'),
        meta:{
            title : 'View payment',
        },
    },
    {
        path: "/settings",
        name: "settings",
        component: ()=>import('./views/SettingsPage.vue'),
        meta:{
            title : 'Settings',
        },
    },
    {
        path: "/referrals",
        name: "referrals",
        component: ()=>import('./views/ReferralsPage.vue'),
        meta:{
            title : 'Referrals',
        },
    },

    {
        path: "/:catchAll(.*)",
        name: "NotFound",
        component: ()=>import('./views/NotFoundPage.vue'),
        meta:{
            title : '404',
            layout : 'MINIMAL',
            public : true,
        },
    },
    ];

    const router = createRouter({
        history: createWebHistory(window.init_account_base),
        routes,
        scrollBehavior(to, from, savedPosition) {
            return { top: 0 };
        },
    });

    const Auth = useAuthStore();
    const ActiveProvider = useActiveProviderStore();

    router.beforeEach(async(to, from, next) => {
        try {
            const logged_in = await Auth.check();
            const active_provider_found = await ActiveProvider.check();
            const going_to_a_secured_route = !(to.meta||{}).public;

            if (!active_provider_found) {
                alert('Invalid account ID. Unable to initialise portal.');
            } else if ( going_to_a_secured_route && !logged_in ) {
                next({name:'Login',query: { redirect: to.path }});
            } else {
                next();
            }
        } catch (error) {
            if ((error.response||{}).status == 401) {
                Auth.logout();
                next({ name: 'Login' , query: { redirect: to.path }});
            }
        }
    })

    router.afterEach((to,from,failure)=>{
        const page_title = (to.meta||{}).title || to.name || 'MISSING PAGE TITLE';
        const provider = unref(ActiveProvider.provider);
        const title_prefix = provider ? ' | '+provider.name  : '';
        document.title = page_title+title_prefix;
    })


    export default router;
